import { useCallback, useEffect, useRef, useState } from "react";
import type { Job } from "@eagledispatch/shared";
import { apiFetch } from "../../lib/api";
import {
  getQueueStatus,
  onQueueChange,
  replayQueue,
  type QueueStatus,
} from "../../lib/offline-queue";
import { useTenantPhone, phoneHref, type TenantPhoneState } from "../../lib/tenant-phone";
import { TENANT_PHONE } from "./constants";
import Button from "../../components/Button";
import Card from "../../components/Card";
import PasskeyEnrollmentCard from "../../components/PasskeyEnrollmentCard";

/**
 * WaitingScreen — driver home when no active job is assigned.
 *
 * Polls GET /drivers/me/active-job every 30s (plus window focus), guarded by a
 * monotonic load sequence so a slow poll can't clobber a newer focus refetch.
 * `{job: null}` → stay; a NEW/IN_PROGRESS job → route to ActiveJob.
 *
 * Queue banner: shows pending/failed offline actions and a "Sync now" trigger.
 * Wired to onQueueChange for live updates.
 *
 * Unit #14: shows a transient `notice` (cancellation / reassignment toast)
 * supplied by the parent. The parent auto-dismisses; the WaitingScreen just
 * renders the banner when one is present and forwards the dismiss action.
 *
 * SPEC-30 + SPEC-61: Clock Out was relocated to the AppChrome toolbar (SPEC-61).
 * WaitingScreen no longer performs the clock-out ceremony; it only renders the
 * notice banner when one is supplied by the parent.
 */
export default function WaitingScreen({
  onJob,
  notice,
  onDismissNotice,
  onToast,
}: {
  onJob: (job: Job) => void;
  notice?: { kind: "cancelled" | "reassigned" | "paused"; message: string } | null;
  onDismissNotice?: () => void;
  /** SPEC-68 §4.15 — forwarded to the shared enrollment card. */
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  const phoneState: TenantPhoneState = useTenantPhone();

  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncedFlash, setSyncedFlash] = useState(false);
  const loadSeqRef = useRef(0);

  const refreshQueue = useCallback(() => {
    void getQueueStatus().then(setQueue);
  }, []);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      const res = await apiFetch<{ job: Job | null }>(
        "/api/v1/drivers/me/active-job",
      );
      if (seq !== loadSeqRef.current) return;
      if (res.job) onJob(res.job);
      else setError(null);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [onJob]);

  useEffect(() => {
    void load();
    refreshQueue();
  }, [load, refreshQueue]);

  useEffect(() => {
    const unsubscribe = onQueueChange(() => {
      refreshQueue();
    });
    return unsubscribe;
  }, [refreshQueue]);

  useEffect(() => {
    const id = window.setInterval(() => void load(), 30_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      loadSeqRef.current += 1;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await replayQueue();
      await refreshQueue();
      setSyncedFlash(true);
      window.setTimeout(() => setSyncedFlash(false), 3000);
    } finally {
      setSyncing(false);
    }
  };

  const pendingCount = queue?.pending ?? 0;
  const failedCount = queue?.failed ?? 0;
  const pendingFinish = queue?.pendingItems.some(
    (a) => a.actionType === "FINISH",
  );
  const pendingStart = queue?.pendingItems.some(
    (a) => a.actionType === "START",
  );

  return (
    <main className="surface driver-waiting">
      {/* SPEC-64b: the old empty surface-header (its h1 read "Waiting") was
          removed — it said the same word as the green banner below and was
          orphaned when SPEC-61 relocated its Clock Out button to the
          toolbar. The banner is the screen's title now. */}

      <Card className="waiting-card">
        {/* SPEC-64b audit BUG #1: heading element, not <p> — this is the
            screen's ONLY heading (the old h1 said "Waiting" and was removed
            as visually redundant; the banner inherits the role). The
            .waiting-title class overrides every h1 visual property
            (34px/800/white/letter-spacing/margin) — zero visual change. */}
        <h1 className="waiting-title">WAITING FOR DISPATCH</h1>
        <p className="muted">You'll be notified when a job is assigned.</p>
      </Card>

      {queue?.unavailable && (
        <Card className="list-banner queue-banner">
          <p className="err">⚠️ Offline sync unavailable</p>
        </Card>
      )}

      {notice && (
        <Card className="list-banner queue-banner">
          <p className="err">
            {notice.kind === "cancelled" ? "⚠️" : "ℹ️"} {notice.message}
          </p>
          {onDismissNotice && (
            <Button variant="ghost" onClick={onDismissNotice}>
              Dismiss
            </Button>
          )}
        </Card>
      )}

      {/* SPEC-68 §4.15 — the shared enrollment card. Self-gating (renders
          nothing unless canEnroll && !hasPasskey); mounted unconditionally so
          the gating lives in one place. Sits after the operational notice and
          before the queue banner: a setup prompt never outranks a real
          banner. */}
      <PasskeyEnrollmentCard onToast={onToast} />

      {pendingCount > 0 && (
        <Card className="list-banner queue-banner">
          <p className="muted">
            📤 {pendingCount} action{pendingCount === 1 ? "" : "s"} waiting to
            sync
            {pendingFinish ? " · Finish queued" : ""}
            {pendingStart ? " · Start queued" : ""}
          </p>
          {queue?.pendingItems.some((a) => a.error) && (
            <p className="err" style={{ margin: "4px 0 0 0" }}>
              ⚠️ {queue.pendingItems.find((a) => a.error)?.error}
            </p>
          )}
          <Button variant="ghost" disabled={syncing} onClick={() => void syncNow()}>
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </Card>
      )}

      {failedCount > 0 && queue?.failedItems.length ? (
        <Card className="list-banner queue-banner">
          <p className="err">
            ⚠️ {failedCount} failed to sync —{" "}
            {queue.failedItems[0].error}
          </p>
        </Card>
      ) : null}

      {syncedFlash && (
        <Card className="list-banner queue-banner">
          <p className="muted">✅ Synced</p>
        </Card>
      )}

      {error && (
        <Card className="list-banner">
          <p className="muted">Can't reach dispatch right now — will retry.</p>
          <Button variant="ghost" onClick={() => void load()}>
            Retry
          </Button>
        </Card>
      )}

      {(() => {
        if (phoneState.status === "ok" && phoneState.phone !== null) {
          const live = phoneState.phone;
          // DISPATCH-PHONE (PM 2026-09-17): the number is dynamic — it is
          // what `tel:` dials (dispatcher → dispatcher-default → office
          // phone, resolved server-side), never painted on the button face.
          // Emoji removed; text centered.
          return (
            <a
              className="btn btn-danger call-button"
              href={phoneHref(live)}
              style={{ textAlign: "center", display: "block" }}
            >
              Call Dispatch
            </a>
          );
        }
        if (phoneState.status === "ok" && phoneState.phone === null) {
          return (
            <span
              className="btn btn-danger call-button call-button--disabled"
              aria-disabled="true"
              style={{ textAlign: "center", display: "block" }}
            >
              Office phone not configured
            </span>
          );
        }
        // loading / fallback: offline-tolerant — dial the TENANT_PHONE
        // constant, but still no emoji and no number on the face.
        return (
          <a
            className="btn btn-danger call-button"
            href={phoneHref(TENANT_PHONE)}
            style={{ textAlign: "center", display: "block" }}
          >
            Call Dispatch
          </a>
        );
      })()}
    </main>
  );
}
